import { IconArrowBackUp } from "@tabler/icons-react"
import { type ReactNode, useState } from "react"

import type { CommunityPostComment } from "@forento/core/models/community"
import { orderCommentThreads } from "@forento/core/utilities/commentThreads"

import { cn } from "../utilities/tailwind"
import CommunityCommentComposer from "./CommunityCommentComposer"

const defaultLabels = {
	reply: "Reply",
	replyTo: (name: string) => `Reply to ${name}`,
	send: "Send reply",
	sending: "Sending…",
	cancel: "Cancel",
	failed: "Could not send your reply. Please try again.",
}

export type CommentThreadPresentation = {
	replyAction: ReactNode
	replyContext: ReactNode
	isReply: boolean
}

/** Keep arbitrary reply depth readable on small screens without losing the direct reply target. */
export default function CommunityCommentThreads({
	comments,
	canReply,
	onReply,
	children,
	labels = defaultLabels,
	compact = false,
}: {
	compact?: boolean
	labels?: typeof defaultLabels
	comments: CommunityPostComment[]
	canReply: boolean
	onReply(parentId: number, text: string): Promise<void>
	children(comment: CommunityPostComment, presentation: CommentThreadPresentation): ReactNode
}) {
	const [targetId, setTargetId] = useState<number | null>(null)
	const [text, setText] = useState("")
	const [isSending, setSending] = useState(false)
	const byId = new Map(comments.map(comment => [comment.id, comment]))

	return (
		<div className="flex min-w-0 flex-col">
			{orderCommentThreads(comments).map(({ comment, depth }) => {
				const parent = comment.parentId === null ? undefined : byId.get(comment.parentId)
				const visibleDepth = Math.min(depth, 2)
				return (
					<div
						key={comment.id}
						id={`comment-${comment.id}`}
						style={{ marginLeft: visibleDepth * 24 }}
						className={cn("relative min-w-0", depth > 0 ? "pt-4 pl-3 sm:pl-5" : "mt-6 first:mt-0")}
					>
						{/* Continue every visible ancestor rail through descendants and their open composers. */}
						{Array.from({ length: visibleDepth }, (_, index) => (
							<span
								key={index}
								aria-hidden="true"
								className="border-light/20 pointer-events-none absolute inset-y-0 border-l"
								style={{ left: (index + 1 - visibleDepth) * 24 }}
							/>
						))}
						{children(comment, {
							isReply: parent !== undefined,
							replyContext: parent ? (
								<span className="text-light inline-flex max-w-full items-center gap-1 text-xs">
									<IconArrowBackUp className="size-3.5 shrink-0" />
									<span className="truncate">
										{labels.replyTo(`${parent.profile.firstName} ${parent.profile.lastName}`)}
									</span>
								</span>
							) : null,
							replyAction: canReply ? (
								<button
									type="button"
									className={cn(
										"hover:text-primary focus-visible:outline-primary inline-flex cursor-pointer items-center gap-1.5 rounded py-1 text-xs font-medium transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-default disabled:opacity-50",
										targetId === comment.id ? "text-primary" : "text-light",
									)}
									disabled={isSending}
									aria-expanded={targetId === comment.id}
									onClick={() => {
										if (targetId === comment.id) return
										setTargetId(comment.id)
										setText("")
									}}
								>
									<IconArrowBackUp className="size-4" />
									{labels.reply}
								</button>
							) : null,
						})}
						{canReply && targetId === comment.id && (
							<CommunityCommentComposer
								key={comment.id}
								className={cn("mt-3", compact ? "ml-[38px]" : parent ? "ml-11" : "ml-[52px]")}
								value={text}
								onChange={setText}
								label={labels.replyTo(`${comment.profile.firstName} ${comment.profile.lastName}`)}
								replyTo={labels.replyTo(`${comment.profile.firstName} ${comment.profile.lastName}`)}
								submitLabel={labels.send}
								sendingLabel={labels.sending}
								errorLabel={labels.failed}
								cancelLabel={labels.cancel}
								onCancel={() => setTargetId(null)}
								isSending={isSending}
								onSubmit={async () => {
									setSending(true)
									try {
										await onReply(comment.id, text.trim())
										setTargetId(null)
										setText("")
									} finally {
										setSending(false)
									}
								}}
							/>
						)}
					</div>
				)
			})}
		</div>
	)
}
